import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  Wrench,
  Wallet,
  ShieldCheck,
  Mountain,
  Briefcase,
  Mail,
  Heart,
} from 'lucide-react';
import { Eyebrow } from '../../design-system/pds';
import { MarketingPageHead } from '../MarketingPageHead';

/**
 * AboutPage
 * --------------------------------------------------------------
 * Worqly's "About" page. Operator-first voice. No fake founder
 * names, no fake team photos — placeholder copy describes the
 * shape of the team and intent of the company.
 *
 * Visual language matches the rest of the marketing site.
 */

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as any } },
};

const STAGGER = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

export function AboutPage() {
  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead
        title="About"
        description="Why Worqly exists, who we're for, and what we will and won't ship. Operator-first software for small business."
      />
      <Hero />
      <Story />
      <Values />
      <Hiring />
      <FooterCTA />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * HERO
 * ───────────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section className="relative pt-12 pb-10 lg:pt-20 lg:pb-14">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div initial="hidden" animate="visible" variants={STAGGER}>
          <motion.div variants={FADE_UP}>
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.10em]
                bg-white/80 text-ink shadow-pill ring-1 ring-ink/5
                dark:bg-white/10 dark:text-white dark:ring-white/10 dark:shadow-none"
            >
              <Sparkles className="h-3 w-3 text-coral" />
              About Worqly
            </span>
          </motion.div>

          <motion.h1
            variants={FADE_UP}
            className="mt-6 font-light text-display-xl text-ink dark:text-white tracking-[-0.035em] leading-[1.02] text-balance"
          >
            Built by operators,{' '}
            <span className="italic font-serif text-coral">for operators.</span>
          </motion.h1>

          <motion.p
            variants={FADE_UP}
            className="mt-6 text-body-lg text-ink/65 dark:text-white/65 max-w-2xl mx-auto text-pretty"
          >
            Small businesses deserve software that respects their time, their margins, and their
            trust. We're building that — one honest module at a time.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * STORY
 * ───────────────────────────────────────────────────────────── */

function Story() {
  const paragraphs = [
    'We started Worqly after spending too many years inside the kind of small business that runs on duct tape — QuickBooks for invoicing, a spreadsheet for payroll, a different spreadsheet for time, and a fourth tool nobody could remember the password to. Every Friday cost half a day of reconciliation. Every customer question cost a tab-hunt.',
    'We were certain the problem was solved already. It isn\'t. Mid-market SaaS treats the small operator as a "starter tier" — a stripped-down version of the enterprise product, sold at a discount, with the workflows of nobody in particular. The result is a tool that grows with you but never quite fits.',
    'So we\'re building from the other end. Invoices first, payroll second, time tracking third — each module deeply complete on its own, designed to share data with the next, and priced so that a 6-person studio pays for what 6 people use. No bundles, no upsells, no annual lock-ins.',
  ];

  return (
    <section className="py-16 lg:py-20 bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
        >
          <motion.div variants={FADE_UP}>
            <Eyebrow className="!text-coral">Our story</Eyebrow>
          </motion.div>
          <motion.h2
            variants={FADE_UP}
            className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
          >
            Software for the businesses{' '}
            <span className="italic font-serif text-coral">SaaS forgot.</span>
          </motion.h2>

          {paragraphs.map((p, i) => (
            <motion.p
              key={i}
              variants={FADE_UP}
              className="mt-6 text-[16px] text-ink/70 dark:text-white/70 leading-[1.75]"
            >
              {p}
            </motion.p>
          ))}

          <motion.div
            variants={FADE_UP}
            className="mt-8 rounded-3xl p-7 bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card"
          >
            <Eyebrow>Worqly today</Eyebrow>
            <ul className="mt-3 grid grid-cols-2 gap-4">
              {[
                { v: '8',  l: 'Modules shipped' },
                { v: '1',  l: 'Continent (for now)' },
                { v: '7',  l: 'Free trial days' },
                { v: '∞',  l: 'Annual lock-in clauses' },
              ].map((s) => (
                <li key={s.l}>
                  <p className="text-stat-lg font-bold tabular-nums leading-none text-ink dark:text-white">{s.v}</p>
                  <p className="mt-1 text-[12.5px] text-ink/55 dark:text-white/55">{s.l}</p>
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * VALUES
 * ───────────────────────────────────────────────────────────── */

function Values() {
  const values = [
    {
      Icon: Wrench,
      title: 'Operator-first',
      body: 'Every feature is shaped by what someone running a 6-person team actually needs on a Tuesday — not what a buyer at an enterprise wants on a quarterly review.',
    },
    {
      Icon: Wallet,
      title: 'Honest pricing',
      body: 'Per-module, per-active-seat, monthly. No bundles, no "Contact us" pages, no surprise overage charges. The number on the website is the number on your card.',
    },
    {
      Icon: ShieldCheck,
      title: 'Privacy by default',
      body: 'Tenant isolation enforced at the database, magic-link auth, no card data on our servers. We carry the smallest customer record we can to do the job.',
    },
    {
      Icon: Mountain,
      title: 'Built to last',
      body: 'We\'re funded to be patient. No race to an exit, no pressure to ship a half-baked AI overlay. The roadmap belongs to the operators who pay the bills.',
    },
  ];

  return (
    <section className="py-20 lg:py-24">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={STAGGER}
          className="max-w-3xl mb-10 lg:mb-12"
        >
          <motion.div variants={FADE_UP}>
            <Eyebrow className="!text-coral">Values</Eyebrow>
          </motion.div>
          <motion.h2
            variants={FADE_UP}
            className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
          >
            The four rules{' '}
            <span className="italic font-serif text-coral">we don't break.</span>
          </motion.h2>
          <motion.p variants={FADE_UP} className="mt-4 text-[15px] text-ink/65 dark:text-white/65 max-w-2xl">
            These show up in every roadmap argument we have. If a feature breaks one of them, we
            don't ship it — we go back and figure out the version that doesn't.
          </motion.p>
        </motion.div>

        <motion.ul
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5"
        >
          {values.map((v) => (
            <motion.li
              key={v.title}
              variants={FADE_UP}
              className="rounded-3xl p-7 bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400">
                <v.Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-[20px] font-semibold text-ink dark:text-white tracking-[-0.01em]">
                {v.title}
              </h3>
              <p className="mt-2 text-[14px] text-ink/65 dark:text-white/65 leading-relaxed">{v.body}</p>
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * HIRING STUB
 * ───────────────────────────────────────────────────────────── */

function Hiring() {
  return (
    <section className="py-20 lg:py-24 bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start"
        >
          <motion.div variants={FADE_UP} className="lg:col-span-7">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400">
                <Briefcase className="h-5 w-5" />
              </span>
              <Eyebrow className="!text-coral">We're hiring</Eyebrow>
            </div>
            <h2 className="mt-4 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance">
              We're small. <span className="italic font-serif text-coral">We hire slowly.</span>
            </h2>
            <p className="mt-4 text-[15px] text-ink/65 dark:text-white/65 leading-relaxed max-w-xl">
              We don't keep a careers page full of evergreen postings. When a real role opens, it
              shows up here with a clear description and a fast process — usually two conversations
              and a paid trial week.
            </p>
            <p className="mt-3 text-[15px] text-ink/65 dark:text-white/65 leading-relaxed max-w-xl">
              If you've operated a small business in the past — bookkeeping, payroll, ops — and
              you can build, we want to talk regardless of whether a role is open.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="mailto:jobs@worqly.io?subject=Operator%20engineer"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-coral text-white text-[13.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
              >
                Email jobs@worqly.io
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to="/about"
                className="inline-flex items-center gap-1 h-11 px-2 text-[13.5px] font-semibold text-ink/70 hover:text-coral dark:text-white/70 dark:hover:text-coral-400 transition-colors"
              >
                Read our values
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>

          <motion.div variants={FADE_UP} className="lg:col-span-5">
            <div className="rounded-3xl bg-ink-900 text-white p-7 ring-1 ring-white/10 shadow-card-lg">
              <Eyebrow className="!text-white/55">Currently hiring</Eyebrow>
              <p className="mt-3 text-[18px] font-semibold leading-tight">
                Nothing open right now — but the door is.
              </p>
              <p className="mt-3 text-[13.5px] text-white/65 leading-relaxed">
                We respond to every thoughtful email. Tell us what you've shipped, what you'd like
                to build, and a small business you've helped operate.
              </p>
              <div className="mt-5 flex items-center gap-2">
                <Mail className="h-4 w-4 text-coral-400" />
                <a
                  href="mailto:jobs@worqly.io"
                  className="text-[13.5px] font-semibold underline-offset-4 hover:underline"
                >
                  jobs@worqly.io
                </a>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Heart className="h-4 w-4 text-coral-400" />
                <span className="text-[13.5px] text-white/65">Remote-friendly</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Footer CTA
 * ───────────────────────────────────────────────────────────── */

function FooterCTA() {
  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <div className="relative rounded-[32px] overflow-hidden bg-coral-gradient text-white p-10 lg:p-16 shadow-card-lg">
          <div aria-hidden className="absolute inset-0 pds-dotted opacity-15 mix-blend-overlay" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">Start free</p>
              <h2 className="mt-3 font-light text-display-md leading-[1.05] tracking-[-0.025em] text-balance">
                Try the product the team here <em className="font-serif">actually uses.</em>
              </h2>
              <p className="mt-4 text-body-lg text-white/85 max-w-xl">
                We run Worqly on Worqly — invoicing, payroll, time, the whole stack. Seven free
                days, no card to start.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-ink-900 text-white text-[14.5px] font-semibold hover:bg-black transition-colors"
              >
                Start free
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white/15 backdrop-blur-md text-white text-[14.5px] font-semibold ring-1 ring-white/25 hover:bg-white/25 transition-colors"
              >
                See pricing
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
